import React from 'react';
import { motion } from 'framer-motion';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { EASE, Reveal } from './ui';
import styles from './ScreenInspector.module.css';

// The smallest test that uses the library, one array per line: [text] or [kind, text].
function libCode(question) {
    return [
        [['k', 'from'], [' screen_inspector '], ['k', 'import'], [' (']],
        [['    ScreenInspector, SqliteStore, TargetLibrary)']],
        [],
        [['inspector = ScreenInspector()']],
        [['ui = TargetLibrary(SqliteStore('], ['s', '"targets/targets.db"'], ['))']],
        [],
        [['r = inspector.'], ['f', 'check'], ['(']],
        [['    shot, '], ['s', `"${question}"`], [',']],
        [['    region=ui['], ['s', '"overview.circulation_pump"'], ['])']],
        [['k', 'assert'], [' r.value '], ['k', 'is'], [' True']],
    ];
}

const TOKEN = { k: styles.tokKey, s: styles.tokStr, f: styles.tokFn };

function Code({ lines }) {
    return (
        <pre className={styles.libCode}>
            {lines.map((segs, i) => (
                <div key={i}>
                    {segs.length === 0 ? ' ' : segs.map((seg, j) => (
                        seg.length === 1
                            ? <span key={j}>{seg[0]}</span>
                            : <span key={j} className={TOKEN[seg[0]]}>{seg[1]}</span>
                    ))}
                </div>
            ))}
        </pre>
    );
}

function Facts({ facts }) {
    return (
        <dl className={styles.facts}>
            {facts.map(([k, v]) => (
                <React.Fragment key={k}>
                    <dt>{k}</dt>
                    <dd>{v.startsWith('pip ') ? <code>{v}</code> : v}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
}

function ChapterText({ part }) {
    return (
        <Reveal className={styles.chapterText}>
            <p className={styles.chapterKicker}>{part.kicker}</p>
            <h2 className={styles.chapterTitle}>{part.title}</h2>
            <p className={styles.chapterBody}>{part.body}</p>
            <Facts facts={part.facts} />
        </Reveal>
    );
}

/** Opening slide of part 1: what the library is, plus the smallest test that uses it. */
export function PartOne({ t }) {
    return (
        <div className={styles.chapter}>
            <ChapterText part={t.part1} />
            <Reveal className={styles.chapterVisual} delay={0.15}>
                <Code lines={libCode(t.how.steps[3].question)} />
            </Reveal>
        </div>
    );
}

/** Opening slide of part 2: what the MCP server is, and the tools the AI walks through. */
export function PartTwo({ t }) {
    const p = t.part2;
    return (
        <div className={styles.chapter}>
            <ChapterText part={p} />
            <div className={styles.chapterVisual}>
                <ol className={styles.flow}>
                    {p.flow.map(([tool, text], i) => (
                        <motion.li
                            key={tool}
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.25 + i * 0.12, ease: EASE }}
                        >
                            <code>{tool}</code>
                            <span>{text}</span>
                            {i < p.flow.length - 1 && <ArrowDownwardRoundedIcon className={styles.flowArrow} sx={{ fontSize: 14 }} />}
                        </motion.li>
                    ))}
                </ol>
                <Reveal delay={0.9}><p className={styles.bench}>{p.bench}</p></Reveal>
            </div>
        </div>
    );
}
